import type { Experience } from '~/lib/experiences/registry';
import type { Locale } from '~/lib/i18n';
import { EMBOSS, TAPE_CLIP } from '~/components/blocks/caseChrome';
import { PrintMarks } from '~/components/PrintMarks';
import { CV_CONTACT } from '~/lib/cvData';
import { experienceRoute, EXPERIENCES } from '~/lib/experiences/registry';

interface Localized {
  nl: string;
  en: string;
}

const DOC = {
  kicker: { nl: 'Selectie · Experiences', en: 'Selection · Experiences' },
  lede: {
    nl: 'Een gedrukte selectie van interactieve 3D-stukken die volledig in de browser draaien. Elk stuk is zowel experiment als eindproduct: een plek om een techniek tot op de pixel uit te werken.',
    en: 'A printed selection of interactive 3D pieces that run entirely in the browser. Each one is both experiment and finished object: a place to work a technique out to the pixel.',
  },
  reference: { nl: 'Projectenbundel', en: 'Projects dossier' },
  issued: { nl: 'Opgemaakt', en: 'Issued' },
  plate: { nl: 'Techniek', en: 'Technique' },
  place: { nl: 'Plaats', en: 'Place' },
  visit: { nl: 'Bekijk live', en: 'View live' },
  needsCamera: { nl: 'Gebruikt je webcam', en: 'Uses your webcam' },
  sheet: { nl: 'Blad', en: 'Sheet' },
} satisfies Record<string, Localized>;

const COUNT_WORDS: Record<Locale, readonly string[]> = {
  nl: ['', 'Eén', 'Twee', 'Drie', 'Vier', 'Vijf', 'Zes', 'Zeven', 'Acht', 'Negen'],
  en: ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'],
};

/**
 * @param locale - Active display locale
 * @returns The cover title, counting whatever the registry holds so it never goes stale
 */
function coverTitle(locale: Locale): string {
  const count = COUNT_WORDS[locale][EXPERIENCES.length] ?? String(EXPERIENCES.length);
  if (1 === EXPERIENCES.length) {
    return 'nl' === locale ? `${count} kleine wereld` : `${count} small world`;
  }

  return 'nl' === locale ? `${count} kleine werelden` : `${count} small worlds`;
}

/** Per-project ink: the design system alternates even cyan-plate / odd red-plate. */
const INK = [
  { plate: 'var(--color-logo-cyan)', counter: 'var(--color-logo-red)' },
  { plate: 'var(--color-logo-red)', counter: 'var(--color-logo-cyan)' },
] as const;

/**
 * @param locale - Active display locale
 * @returns A reader for the document's own copy and the registry's localized fields
 */
function text(locale: Locale) {
  return {
    t: (key: keyof typeof DOC) => DOC[key][locale],
    loc: (obj: Localized) => obj[locale],
  };
}

/**
 * @param label - Tape text
 * @param ink - Plate colour of the current project, tinting the tape shadow
 * @returns A section label on straight label-maker tape, matching the CV print idiom
 */
function TapeLabel({ label, ink }: { label: string; ink: string }) {
  return (
    <span
      className={`inline-block shrink-0 bg-maroon-900 bg-grain px-3 py-1 shadow-[inset_0_1px_0_oklch(1_0_0/0.14),inset_0_-2px_0_oklch(0.1_0.03_20/0.5)] print:py-0.5 ${TAPE_CLIP}`}
      style={{ boxShadow: `3px 3px 0 ${ink}` }}
    >
      <span className={`text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-paper print:[text-shadow:none] ${EMBOSS}`}>
        {label}
      </span>
    </span>
  );
}

/**
 * @param locale - Active display locale
 * @param issued - Localised issued date
 * @returns The cover sheet: brand mark, name, title off register and a short framing lede
 */
function CoverSheet({ locale, issued }: { locale: Locale; issued: string }) {
  const { t } = text(locale);
  const title = coverTitle(locale);

  return (
    <section
      aria-label={title}
      className="pdf-sheet relative flex w-full flex-col justify-between overflow-hidden bg-paper bg-grain px-10 py-12 md:px-16 md:py-16 print:px-12 print:py-14"
    >
      <PrintMarks className="text-maroon-900/40" />

      <div className="relative flex items-center justify-between">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-maroon-500">
          {t('kicker')}
        </span>
        <span
          className="size-12 bg-logo-red md:size-14"
          style={{
            maskImage: 'url(/logo/jdn-icon.svg)',
            WebkitMaskImage: 'url(/logo/jdn-icon.svg)',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }}
          aria-hidden
        />
      </div>

      <div className="relative">
        <h1 className="font-display text-5xl leading-[0.95] tracking-[1px] text-maroon-900 [text-shadow:0.04em_0.035em_0_var(--color-logo-red)] md:text-7xl print:text-6xl">
          {title}
        </h1>
        <p className="mt-6 max-w-[48ch] text-base leading-relaxed text-maroon-700 md:text-lg print:mt-5 print:text-sm print:leading-relaxed">
          {t('lede')}
        </p>
      </div>

      <div className="relative flex flex-wrap items-end justify-between gap-6 border-t-2 border-maroon-900 pt-5 shadow-[0_3px_0_var(--color-logo-red),0_6px_0_var(--color-logo-cyan)]">
        <div>
          <p className="font-display text-2xl tracking-[0.5px] text-maroon-900">{CV_CONTACT.name}</p>
          <p className="mt-1 text-sm text-maroon-600">{CV_CONTACT.website}</p>
        </div>
        <div className="text-right text-[0.65rem] uppercase tracking-[0.2em] text-maroon-400">
          <p>{t('reference')}</p>
          <p className="mt-1 tabular-nums">
            {t('issued')}
            {' · '}
            {issued}
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * @param project - The experience to lay out
 * @param index - Zero-based sheet index, selecting the alternating ink
 * @param locale - Active display locale
 * @returns A single project sheet: framed cover image, off-register number, summary and technique tape
 */
function ProjectSheet({
  project,
  index,
  locale,
}: {
  project: Experience;
  index: number;
  locale: Locale;
}) {
  const { t, loc } = text(locale);
  const ink = INK[index % 2];
  const number = String(index + 1).padStart(2, '0');
  const href = `${CV_CONTACT.website}${experienceRoute(project.slug)}`;

  return (
    <section
      aria-label={project.title}
      className="pdf-sheet relative flex w-full flex-col overflow-hidden bg-paper bg-grain px-10 py-12 md:px-16 md:py-14 print:px-12 print:py-12"
    >
      <PrintMarks className="text-maroon-900/40" />

      <header className="relative flex items-start justify-between gap-6">
        <div>
          <TapeLabel label={`${t('sheet')} ${number}`} ink={ink.plate} />
          <h2 className="mt-4 font-display text-4xl tracking-[0.5px] text-maroon-900 md:text-5xl print:text-4xl" style={{ textShadow: `0.035em 0.03em 0 ${ink.counter}` }}>
            {project.title}
          </h2>
          <p className="mt-2 text-sm italic text-maroon-500">
            {t('place')}
            {' · '}
            {loc(project.place)}
          </p>
        </div>
        <span
          aria-hidden
          className="font-display text-6xl leading-none text-maroon-900/10 md:text-8xl print:text-7xl"
        >
          {number}
        </span>
      </header>

      <div className="relative mt-8 grid flex-1 grid-cols-1 gap-8 md:grid-cols-[1.35fr_1fr] md:items-center print:mt-7">
        <figure className="relative">
          <span
            aria-hidden
            className="absolute -top-2 -left-2 h-full w-full rounded-[3px]"
            style={{ boxShadow: `-6px -4px 0 ${ink.counter}, 9px 9px 0 ${ink.plate}`, border: '2px solid var(--color-maroon-900)' }}
          />
          <div className="relative overflow-hidden rounded-[3px] border-2 border-maroon-900">
            <img
              src={project.cover.src}
              alt={project.title}
              width={project.cover.width}
              height={project.cover.height}
              className="block aspect-[4/3] w-full object-cover"
              style={{ objectPosition: project.cover.position }}
            />
          </div>
        </figure>

        <div>
          <p className="max-w-[46ch] text-base leading-relaxed text-maroon-800 print:text-sm print:leading-relaxed">
            {loc(project.summary)}
          </p>

          <div className="mt-7 print:mt-6">
            <TapeLabel label={t('plate')} ink={ink.plate} />
            <ul className="mt-3 flex flex-wrap gap-2" role="list">
              {project.techniques.map(technique => (
                <li
                  key={technique}
                  className="rounded-[2px] border border-maroon-300 px-2.5 py-1 text-xs font-medium tracking-wide text-maroon-700"
                >
                  {technique}
                </li>
              ))}
            </ul>
          </div>

          {true === project.needsCamera && (
            <p className="mt-4 text-xs uppercase tracking-[0.14em] text-maroon-400">{t('needsCamera')}</p>
          )}
        </div>
      </div>

      <footer className="relative mt-8 flex items-center justify-between border-t border-maroon-900/20 pt-4 print:mt-6">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-maroon-500">
          {t('visit')}
        </span>
        <span className="text-sm tabular-nums text-maroon-700">{href}</span>
      </footer>
    </section>
  );
}

/**
 * The print-first projects dossier: a cover sheet and one sheet per experience, laid out in the
 * two-ink print language and ready for the browser's Save-as-PDF.
 *
 * @param locale - Active display locale
 * @param issued - Localised issued date, stamped by the route loader
 * @returns The full document: a cover sheet plus one sheet per experience
 */
export function ProjectsDocument({ locale, issued }: { locale: Locale; issued: string }) {
  return (
    <div className="pdf-doc relative min-h-screen bg-maroon-950 bg-grain font-sans text-maroon-900 print:bg-none">
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-8 print:max-w-none print:gap-0 print:p-0">
        <CoverSheet locale={locale} issued={issued} />
        {EXPERIENCES.map((project, i) => (
          <ProjectSheet key={project.slug} project={project} index={i} locale={locale} />
        ))}
      </div>
    </div>
  );
}
